import { useEffect, useState } from "react";
import { Plus, Check, Users } from "lucide-react";
import { clientApi, formatCOP } from "../lib/api";
import { useCart } from "../context/CartContext";

// "Los clientes también piden": complementos calculados con el histórico real
// de pedidos de Arcos. Se usa en la ficha del producto y en el carrito.
export const ComplementStrip = ({ codigo, codigos, title = "Los clientes también piden", compact = false }) => {
  const [items, setItems] = useState([]);
  const { add, items: cartItems } = useCart();
  const [justAdded, setJustAdded] = useState({});

  useEffect(() => {
    let cancelled = false;
    const req = codigo
      ? clientApi.get(`/portal/products/${encodeURIComponent(codigo)}/complementos`)
      : (codigos || []).length
        ? clientApi.post("/portal/cart/complementos", { codigos })
        : null;
    if (!req) { setItems([]); return; }
    req.then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [codigo, JSON.stringify(codigos || [])]);

  if (!items.length) return null;

  const inCart = (c) => cartItems.some((i) => i.codigo === c);

  const addOne = (p) => {
    add({
      codigo: p.codigo,
      descripcion: p.descripcion,
      precio: p.precio,
      precio_sin_iva: p.precio_sin_iva,
      unidad: p.unidad,
      imagen: p.imagen,
      categoria: p.sublinea || p.linea,
      iva_porcentaje: p.iva_porcentaje,
      iva_incluido: true,
    }, 1);
    setJustAdded((prev) => ({ ...prev, [p.codigo]: true }));
  };

  return (
    <div className={compact ? "pt-4 mt-2 border-t border-cm-border" : "pt-5 mt-5 border-t border-cm-border"} data-testid="complement-strip">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-3.5 w-3.5 text-cm-red" />
        <h4 className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">{title}</h4>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map((p) => (
          <div
            key={p.codigo}
            className="group rounded-lg border border-cm-border bg-white overflow-hidden hover:border-cm-ink/40 hover:shadow-sm transition-all"
            data-testid={`complement-${p.codigo}`}
          >
            <div className="aspect-square bg-cm-cream-alt/60 overflow-hidden">
              {p.imagen ? (
                <img
                  src={p.imagen}
                  alt={p.descripcion}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-[10px] font-mono text-cm-ink-soft px-2 text-center">
                  {p.sublinea || p.codigo}
                </div>
              )}
            </div>
            <div className="p-2">
              <p className="text-[11px] leading-tight text-cm-ink line-clamp-2 min-h-[26px]" title={p.descripcion}>{p.descripcion}</p>
              <div className="mt-1.5 flex items-center justify-between gap-1">
                <span className="font-mono text-xs font-semibold">{formatCOP(p.precio || 0)}</span>
                <button
                  onClick={() => addOne(p)}
                  className={`h-6 w-6 rounded-full flex items-center justify-center transition-colors ${
                    inCart(p.codigo) || justAdded[p.codigo]
                      ? "bg-emerald-600 text-white"
                      : "bg-cm-ink text-cm-cream hover:bg-cm-red"
                  }`}
                  title={inCart(p.codigo) ? "Ya está en el pedido" : "Agregar al pedido"}
                  data-testid={`complement-add-${p.codigo}`}
                >
                  {inCart(p.codigo) || justAdded[p.codigo] ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
